import { Editor as TinyMCE } from '@tinymce/tinymce-react';
import cx from 'clsx';
import React, { type FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Editor as TinyMCEEditor } from 'tinymce';
import 'tinymce/icons/default';
import 'tinymce/models/dom/model';
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/autoresize';
import 'tinymce/plugins/image';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/media';
import 'tinymce/plugins/quickbars';
import 'tinymce/plugins/wordcount';
import 'tinymce/skins/ui/oxide/content.inline.min.css';
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/themes/silver';

import loaderGif from '@assets/images/loader.gif';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useFileManagerUploadFileMutation } from '@services/useFileManagerUploadFileMutation';

import { selectAppLanguage, selectAppRtl } from '@store/useAppStore';
import { selectAppPersistDarkMode } from '@store/useAppStorePersist';

import LoaderGif from '../LoaderGif/LoaderGif';
import { useFileManagerImgUploadMutation } from './services/useFileManagerImgUploadMutation';
import style from './style.module.scss';
import './tinyMceI18n/ar.js';
import './tinyMceI18n/ca.js';
import './tinyMceI18n/de.js';
import './tinyMceI18n/es.js';
import './tinyMceI18n/fr.js';
import './tinyMceI18n/it.js';
import './tinyMceI18n/ja.js';
import './tinyMceI18n/uk.js';

interface Props {
  value: string;
  placeholder?: string;
  height?: string | number;
  simple: boolean;
  disabled?: boolean;
  hideMedia: boolean;
  onChange?: (value: string) => void;
}

const Editor: FC<Props> = ({ value, placeholder, height, disabled, onChange, simple, hideMedia }) => {
  // Workaround for correct working with uploaded images
  const [initialValue] = useState(value);
  const editorRef = useRef<TinyMCEEditor | null>(null);
  const language = selectAppLanguage();
  const { customerId } = useParams();
  const { t } = useTranslation();

  const { mutate: fileManagerUploadFileMutation, isLoading: isFileUploadLoading } =
    useFileManagerUploadFileMutation();
  const { mutate: fileManagerImgUploadMutation, isLoading: isImageUploadLoading } =
    useFileManagerImgUploadMutation();

  const rtl = selectAppRtl();

  const darkMode = selectAppPersistDarkMode();

  const initInstanceCallback = (editor: any) => {
    editor.on('OpenWindow', function () {
      const findUploadModal = document.querySelector('.tox-dialog .tox-form__controls-h-stack');
      const messageWrap = document.createElement('div');
      messageWrap.style.cssText =
        'position: absolute; top: 55%; margin: 0 auto;text-align:center;font-family:Roboto; font-size:12px;color:#797F89';
      messageWrap.innerHTML = t('Maximum upload file size: 256 MB');
      if (findUploadModal) {
        findUploadModal.append(messageWrap);
        const browseBtn = findUploadModal.getElementsByTagName('button')[0];
        browseBtn.setAttribute('title', 'Browse');
      }
    });
  };
  useEffect(() => {
    const findUploadModal = document.querySelector('.tox-dialog .tox-form__controls-h-stack');
    if (findUploadModal && isFileUploadLoading) {
      const loaderWrap = document.createElement('div');
      const loaderImg = document.createElement('img');
      loaderWrap.style.cssText =
        'width: 100%;position: absolute;top: 25%;left: 0;background: #fff;height: 50%;';
      loaderWrap.id = 'loader-wrap';
      loaderImg.src = loaderGif;
      loaderImg.style.cssText = 'width: 50px;display: block;margin: 35px auto;';
      loaderWrap.append(loaderImg);
      findUploadModal.append(loaderWrap);
    }
  }, [isFileUploadLoading]);

  const filePickerCallback = (callback: any, value: any, meta: any) => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'media');

    input.addEventListener('change', (ev) => {
      const file = (ev.target as HTMLInputElement).files?.[0];
      if (file) {
        if (file.size > 268435456) {
          showToast({ variant: 'error', message: t('Maximum upload file size: 256 MB') });
        } else {
          const formData = new FormData();
          formData.append('file', file);
          fileManagerUploadFileMutation(
            { formData, customerId },
            {
              onSuccess: (data, variables) => {
                if (meta.filetype == 'media') {
                  callback(`${data.data?.file.url}`);
                  const removeWrap = document.getElementById('loader-wrap');
                  const subMenu = document.getElementsByClassName('tox-toolbar__overflow')[0];
                  subMenu?.classList.remove('tox-toolbar__overflow--open');
                  subMenu?.classList.add('tox-toolbar__overflow--closed');
                  removeWrap?.remove();
                }
              },
            },
          );
        }
      }
    });

    input.click();
  };

  const toolbar = simple
    ? false
    : `h1 h2 bold italic underline forecolor | numlist bullist ${hideMedia ? '' : '| quickimage media'}`;

  const handleImageUpload = (blobInfo: any): Promise<string> => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('image', blobInfo.blob());
      fileManagerImgUploadMutation(
        { formData },
        {
          onSuccess: (data: any) => {
            if (data.data?.data?.path) {
              resolve(data.data.data.path);
            }
          },
          onError: (data: any) => {
            reject(data);
          },
        },
      );
    });
  };

  return (
    <div className={cx(style.editorWrapper, { [style.simple]: simple })}>
      {isImageUploadLoading && (
        <div className={cx(style.loaderWrapper, { [style.loaderWrapperSimple]: simple })}>
          <LoaderGif className={style.loader} />
        </div>
      )}
      <TinyMCE
        apiKey={APP_TINYMCE_API_KEY}
        onInit={(evt, editor) => (editorRef.current = editor)}
        initialValue={initialValue}
        init={{
          height: height || 153,
          language: language,
          plugins: simple
            ? ['autolink', 'image', 'quickbars', 'autoresize']
            : ['advlist', 'autolink', 'lists', 'quickbars', 'media', 'wordcount'],
          toolbar: toolbar,
          color_default_foreground: darkMode ? 'White' : 'Black',
          skin: false,
          menubar: false,
          media_poster: false,
          media_alt_source: false,
          quickbars_insert_toolbar: false,
          quickbars_selection_toolbar: simple ? 'h1 h2 bold italic' : false,
          quickbars_image_toolbar: false,
          toolbar_mode: 'sliding',
          statusbar: false,
          image_description: false,
          font_formats:
            '@import url("https://fonts.googleapis.com/css2?family=Roboto&display=swap"); Roboto=Roboto;',
          content_style: `@import url("https://fonts.googleapis.com/css2?family=Roboto&display=swap"); body { font-family:Roboto; font-size:12px; line-height: 18px; ${
            simple
              ? 'padding-left: 7px !important; padding-right: 20px !important;'
              : 'margin: 0; padding: 15px;'
          } ${rtl ? 'padding-right: 25px;' : ''} color: ${
            darkMode ? '#EDEDEF' : '#000' // --inputs-filled-text
          };} img { max-width: 130px; height: auto; } img[src*='blob:'] {display: none;} p {margin: 0} ::-webkit-scrollbar {width: 6px;} ::-webkit-scrollbar-track {background: #f1f1f1;} ::-webkit-scrollbar-thumb {background: #888;} .mce-offscreen-selection {display: none} video {width: 135px; height: 80px}`,
          resize: simple,
          block_unsupported_drop: false,
          content_css: false,
          min_height: 30,
          max_height: 500,
          placeholder,
          autoresize_bottom_margin: 0,
          media_dimensions: false,
          file_picker_types: 'file image media',
          file_picker_callback: filePickerCallback,
          init_instance_callback: initInstanceCallback,
          images_upload_handler: handleImageUpload,
        }}
        onEditorChange={onChange}
        disabled={disabled || isImageUploadLoading}
      />
    </div>
  );
};

export default Editor;
